import React, { useEffect, useMemo, useState } from 'react';
import { Mail } from 'lucide-react';

interface GenerationProgressProps {
  /** 0–100 from the backend job row. */
  progress: number;
  /** Epoch ms when the job started, for the elapsed-time counter. Defaults to mount time. */
  startedAt: number | null;
  /** Singular noun for the filing pool, e.g. "comment" or "reply comment". */
  filingNoun: string;
  /** How many filings are in the pool — shown beneath the stage label. */
  filingCount: number;
  /** Topics the user asked about — surfaced so they can verify the run while it's working. */
  topics: string[];
}

const GRID_SIZE = 7;

interface Dot {
  row: number;
  col: number;
  delay: number;
}

const DOTS: Dot[] = (() => {
  const arr: Dot[] = [];
  const center = (GRID_SIZE - 1) / 2;
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      const dx = r - center;
      const dy = c - center;
      const distance = Math.sqrt(dx * dx + dy * dy);
      arr.push({ row: r, col: c, delay: distance * 0.18 });
    }
  }
  return arr;
})();

const stageFromProgress = (progress: number, elapsedSeconds: number): string => {
  if (progress >= 100) return 'Finalizing…';
  if (progress >= 85) return 'Drafting your summary…';
  if (progress >= 50) return 'Identifying themes and positions…';
  // Backend currently jumps 0 → 50 → 100, so use elapsed time to give the
  // user a sense of motion during the long Stage 1 (extraction) leg.
  if (elapsedSeconds < 20) return 'Reading filings…';
  if (elapsedSeconds < 60) return 'Extracting stakeholder positions…';
  if (elapsedSeconds < 120) return 'Cross-referencing positions across filings…';
  return 'Almost there — drafting your summary…';
};

const formatElapsed = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

export const GenerationProgress: React.FC<GenerationProgressProps> = ({
  progress,
  startedAt,
  filingNoun,
  filingCount,
  topics,
}) => {
  const start = useMemo(() => startedAt ?? Date.now(), [startedAt]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = Math.max(0, Math.floor((now - start) / 1000));
  const stage = stageFromProgress(progress, elapsed);

  // Soften the progress bar — backend gives sparse updates (0/50/100), so we
  // blend in elapsed time as a fallback so the bar always feels alive.
  const elapsedFraction = Math.min(elapsed / 180, 0.85); // 3 min target
  const displayProgress = Math.max(progress, Math.floor(elapsedFraction * 100));

  const cleanTopics = topics.map((t) => t.trim()).filter(Boolean);

  return (
    <div className="overflow-hidden rounded-2xl border border-cg-border bg-gradient-to-b from-cg-cream/50 to-white p-8 sm:p-12">
      <style>{`
        @keyframes cg-dot-pulse {
          0%, 100% { transform: scale(0.32); opacity: 0.18; }
          50% { transform: scale(1); opacity: 1; }
        }
        @keyframes cg-halo {
          0%, 100% { transform: scale(0.88); opacity: 0.25; }
          50% { transform: scale(1.08); opacity: 0.55; }
        }
        @keyframes cg-bar-shimmer {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(120%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cg-dot, .cg-halo, .cg-shimmer { animation: none !important; }
        }
      `}</style>

      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        {/* Animated visual */}
        <div className="relative mb-9 flex h-56 w-56 items-center justify-center">
          {/* Soft breathing halo */}
          <div
            className="cg-halo absolute inset-0 rounded-full bg-cg-green/35 blur-3xl"
            style={{ animation: 'cg-halo 3.6s ease-in-out infinite' }}
          />
          {/* Dot grid — concentric pulse waves */}
          <div
            className="relative grid"
            style={{
              gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(0, 1fr))`,
              gap: '14px',
            }}
          >
            {DOTS.map(({ row, col, delay }) => (
              <div
                key={`${row}-${col}`}
                className="cg-dot h-2.5 w-2.5 rounded-full bg-cg-green"
                style={{ animation: `cg-dot-pulse 2.4s ease-in-out ${delay}s infinite` }}
              />
            ))}
          </div>
        </div>

        {/* Heading */}
        <h3 className="font-display text-2xl font-medium text-cg-ink sm:text-3xl">
          Generating your summary
        </h3>
        <p className="mt-3 text-sm font-medium text-cg-green">{stage}</p>
        <p className="mt-1 text-xs text-neutral-500">
          {filingCount} {filingNoun}
          {filingCount === 1 ? '' : 's'} · {cleanTopics.length} topic
          {cleanTopics.length === 1 ? '' : 's'}
        </p>

        {/* Progress bar */}
        <div className="mt-7 w-full">
          <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-neutral-200/70">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-cg-green transition-all duration-700 ease-out"
              style={{ width: `${Math.max(4, Math.min(100, displayProgress))}%` }}
            />
            <div
              className="cg-shimmer absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent"
              style={{ animation: 'cg-bar-shimmer 2.2s linear infinite' }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-neutral-500">
            <span>{formatElapsed(elapsed)} elapsed</span>
            <span>~3 min total</span>
          </div>
        </div>

        {/* Email reassurance */}
        <div className="mt-7 flex items-start gap-3 rounded-xl border border-cg-border/70 bg-white px-4 py-3 text-left">
          <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-cg-green" />
          <p className="text-xs leading-relaxed text-neutral-600">
            We'll email you a link too — feel free to step away. Your summary will keep generating
            and appear right here when it's ready.
          </p>
        </div>

        {/* Topic peek — gives the user something to verify while they wait */}
        {cleanTopics.length > 0 && (
          <div className="mt-5 w-full">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-neutral-400">
              Topics in this run
            </p>
            <div className="flex flex-wrap justify-center gap-1.5">
              {cleanTopics.slice(0, 8).map((topic, i) => (
                <span
                  key={i}
                  className="rounded-full border border-cg-border bg-white px-2.5 py-1 text-xs text-cg-ink"
                >
                  {topic}
                </span>
              ))}
              {cleanTopics.length > 8 && (
                <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-neutral-500">
                  +{cleanTopics.length - 8} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerationProgress;
